import { Button, HStack, Spinner, Text, VStack } from '@chakra-ui/react';
import { useAppSelector } from '@hooks/redux.hooks';
import { selectSituationTime } from '@redux/situation/situation.selectors';
import { useUpdateEffectiveTimeOffMutation } from '@services/c2/c2.api';
import { HolopticsSubCameraEnum } from '@utils/sensors/camera/camera.constants';
import { getTimeBeforeTurnOff } from '@utils/sensors/camera/camera.utils';
import { useState } from 'react';
import { FormattedMessage } from 'react-intl';

import { HolopticsSensorConfiguration } from '@/types/sensor/sensors';

type Props = {
  cameraConfiguration: HolopticsSensorConfiguration;
  replayMode: boolean;
  displayedCameraId: HolopticsSubCameraEnum.MWIR | HolopticsSubCameraEnum.SWIR;
};

const THRESHOLD_DISPLAY_POPUP_MILLIS = 5 * 60 * 1000;

export default function AutomaticTurnOffPopup({ cameraConfiguration, replayMode, displayedCameraId }: Readonly<Props>) {
  const situationTime = useAppSelector((state) => selectSituationTime(state, replayMode));
  const [updateEffectiveTimeOff] = useUpdateEffectiveTimeOffMutation();
  const [isLoading, setIsLoading] = useState(false);
  const subCameraConfiguration = cameraConfiguration.subCameras.find((sub) => sub.cameraId === displayedCameraId);

  const [previousDisplayExtendTimePopup] = useState<boolean>(subCameraConfiguration?.displayExtendTimePopup ?? false);

  const timeBeforeTurnOff =
    subCameraConfiguration && getTimeBeforeTurnOff(subCameraConfiguration.effectiveTurnOff, situationTime);
  if (
    replayMode ||
    subCameraConfiguration === undefined ||
    !subCameraConfiguration.displayExtendTimePopup ||
    !timeBeforeTurnOff ||
    timeBeforeTurnOff > THRESHOLD_DISPLAY_POPUP_MILLIS ||
    timeBeforeTurnOff < 0
  ) {
    return null;
  }

  const timeBeforeTurnOffString = new Date(timeBeforeTurnOff).toISOString().substring(14, 19);

  if (previousDisplayExtendTimePopup !== subCameraConfiguration.displayExtendTimePopup) {
    setIsLoading(false);
    return null;
  }

  const handleClick = (value: boolean) => {
    setIsLoading(true);
    updateEffectiveTimeOff({
      site: cameraConfiguration.site,
      appCode: cameraConfiguration.appCode,
      id: cameraConfiguration.id,
      subCameraId: displayedCameraId,
      displayExtendTimePopup: value,
    })
      .unwrap()
      .finally(() => {
        setIsLoading(false);
      });
  };

  return (
    <VStack
      position="absolute"
      top="50%"
      left="50%"
      transform="translate(-50%, -50%)"
      padding={4}
      gap={2}
      backgroundColor="neutral.800"
      width="500px"
    >
      {isLoading ? (
        <Spinner size="md" color="neutral.white" />
      ) : (
        <>
          <Text>
            <FormattedMessage id="cameras.effectiveTurnOff" values={{ value: timeBeforeTurnOffString }} />
          </Text>
          <HStack width="100%" gap={2} justifyContent="center">
            <Button variant="secondary" onClick={() => handleClick(false)}>
              <FormattedMessage id="cameras.extend" values={{ extend: false }} />
            </Button>
            <Button onClick={() => handleClick(true)}>
              <FormattedMessage id="cameras.extend" values={{ extend: true }} />
            </Button>
          </HStack>
        </>
      )}
    </VStack>
  );
}
